import { useState, useEffect, useCallback } from 'react';

// 사용자 행동 추적 및 로그인 유도 타이밍 관리
const useAuthPrompt = (isLoggedIn) => {
  const [userMetrics, setUserMetrics] = useState({
    solvedQuestions: 0,
    wrongAnswers: 0,
    sessionMinutes: 0,
    pageViews: 0,
    lastPromptTime: null,
    promptDismissCount: 0
  });

  const [promptConfig, setPromptConfig] = useState({
    shouldShow: false,
    type: null,
    priority: 'low'
  });

  // 프롬프트 표시 규칙
  const PROMPT_RULES = {
    // 첫 번째 트리거: 가벼운 제안
    earlyEngagement: {
      condition: (m) => m.solvedQuestions === 5,
      type: 'teaser',
      priority: 'low',
      message: '진도를 저장하고 싶으신가요?'
    },
    
    // 주요 트리거: 가치 제안
    progressSave: {
      condition: (m) => m.solvedQuestions === 10,
      type: 'progress',
      priority: 'medium',
      message: '학습 진도를 영구 저장하세요'
    },
    
    wrongAnswerTracking: {
      condition: (m) => m.wrongAnswers >= 3,
      type: 'wrongAnswer',
      priority: 'high',
      message: '오답노트로 약점을 관리하세요'
    },
    
    timeInvestment: {
      condition: (m) => m.sessionMinutes >= 20,
      type: 'statistics',
      priority: 'medium',
      message: '학습 통계를 확인하세요'
    },
    
    // 재참여 트리거
    returningUser: {
      condition: (m) => m.pageViews > 20 && m.promptDismissCount < 3,
      type: 'returning',
      priority: 'high',
      message: '자주 방문하시네요! 회원가입하고 더 많은 혜택을 받으세요'
    }
  };

  // 메트릭 업데이트
  const updateMetric = useCallback((metric, value) => {
    if (isLoggedIn) return;
    
    setUserMetrics(prev => ({
      ...prev,
      [metric]: typeof value === 'function' ? value(prev[metric]) : value
    }));
  }, [isLoggedIn]);

  // 문제 풀이 추적
  const trackQuestionSolved = useCallback((isCorrect) => {
    updateMetric('solvedQuestions', v => v + 1);
    if (!isCorrect) {
      updateMetric('wrongAnswers', v => v + 1);
    }
    
    // Google Analytics 이벤트 전송
    if (window.trackQuestionAnswer) {
      window.trackQuestionAnswer(`question_${Date.now()}`, isCorrect);
    }
  }, [updateMetric]);

  // 세션 시간 추적
  useEffect(() => {
    if (isLoggedIn) return;
    
    const startTime = Date.now();
    const interval = setInterval(() => {
      const elapsed = Math.floor((Date.now() - startTime) / 60000);
      updateMetric('sessionMinutes', elapsed);
    }, 60000); // 1분마다 업데이트

    return () => clearInterval(interval);
  }, [isLoggedIn, updateMetric]);

  // 프롬프트 조건 체크
  useEffect(() => {
    if (isLoggedIn) return;

    // 마지막 프롬프트로부터 최소 5분 경과 체크
    const lastPrompt = userMetrics.lastPromptTime;
    if (lastPrompt && Date.now() - lastPrompt < 5 * 60 * 1000) {
      return;
    }

    // 조건 체크 및 우선순위 결정
    let highestPriority = null;
    let selectedRule = null;

    Object.entries(PROMPT_RULES).forEach(([key, rule]) => {
      if (rule.condition(userMetrics)) {
        if (!highestPriority || 
            getPriorityValue(rule.priority) > getPriorityValue(highestPriority.priority)) {
          highestPriority = rule;
          selectedRule = key;
        }
      }
    });

    if (highestPriority) {
      setPromptConfig({
        shouldShow: true,
        type: highestPriority.type,
        priority: highestPriority.priority,
        message: highestPriority.message
      });
      
      updateMetric('lastPromptTime', Date.now());
    }
  }, [userMetrics, isLoggedIn, updateMetric]);

  // 우선순위 값 변환
  const getPriorityValue = (priority) => {
    const values = { low: 1, medium: 2, high: 3 };
    return values[priority] || 0;
  };

  // 프롬프트 닫기
  const dismissPrompt = useCallback(() => {
    setPromptConfig(prev => ({ ...prev, shouldShow: false }));
    updateMetric('promptDismissCount', v => v + 1);
  }, [updateMetric]);

  // 소셜 로그인 성공률 추적
  const trackLoginAttempt = useCallback((provider, success) => {
    // Analytics 이벤트 전송
    if (window.gtag) {
      window.gtag('event', 'login_attempt', {
        method: provider,
        success: success,
        trigger_type: promptConfig.type,
        session_minutes: userMetrics.sessionMinutes,
        questions_solved: userMetrics.solvedQuestions
      });
    }
  }, [promptConfig.type, userMetrics]);

  return {
    userMetrics,
    promptConfig,
    trackQuestionSolved,
    dismissPrompt,
    trackLoginAttempt,
    updateMetric
  };
};

export default useAuthPrompt;